/**
 * tourSteps — content + structure for the iCheck product tour.
 *
 * Structure is DERIVED, not duplicated
 * ------------------------------------
 * The page-level steps are GENERATED from the sidebar config
 * (`getNavForRole` + `SECONDARY_ITEMS` in nav-config). That means the tour's
 * page order, role scoping and RBAC feature-gating always match the sidebar
 * automatically — there is no second hand-maintained list to drift out of sync
 * (which is what caused earlier ordering / wrong-role bugs).
 *
 * What you actually edit here:
 *   - PAGE_COPY  — the prose for each page, keyed by its route (relative to the
 *                  role base). Add an entry to describe a new sidebar page; its
 *                  order/role/visibility come from nav-config for free.
 *   - *_SUBSTEPS — extra steps shown right after a page (filters, the record
 *                  deep-dives). These aren't sidebar items, so they're explicit.
 *   - the shell steps (welcome / navigation / search / finish).
 *
 * Anchors: steps highlight stable `data-tour="<id>"` attributes (see
 * TOUR_ANCHORS). `content` is the <main> region present on every page — the
 * default whole-page highlight. A missing anchor is handled gracefully by the
 * provider (skip / centered popover); the tour never crashes.
 */
import {
  getNavForRole,
  SECONDARY_ITEMS,
  ROLE_BASE,
  type Role,
} from "@/components/layouts/nav-config";

/** Stable anchor ids — add the matching `data-tour={...}` where noted. */
export const TOUR_ANCHORS = {
  // Shell (Header / Sidebar)
  nav: "nav",
  mobileNav: "mobile-nav",
  search: "search",
  askAi: "ask-ai",
  notifications: "notifications",
  syncStatus: "sync-status",
  theme: "theme",
  profile: "profile",
  // <main> region present on every page — used for whole-page highlights.
  content: "content",
  // Inspections list features
  inspectionsActions: "inspections-actions",
  inspectionsFilters: "inspections-filters",
  inspectionRow: "inspection-row",
  // Inspection detail / question workflow
  inspectionStatusActions: "inspection-status-actions",
  qSidebar: "q-sidebar",
  qFilters: "q-filters",
  qTabs: "q-tabs",
  qFooter: "q-footer",
  // Observations list + detail
  observationsFilters: "observations-filters",
  observationRow: "observation-row",
  observationStatus: "observation-status",
  observationRectifications: "observation-rectifications",
  // Vessels list features
  vesselsActions: "vessels-actions",
  vesselsSearch: "vessels-search",
  // Companies list features
  companiesActions: "companies-actions",
  companiesRowActions: "companies-row-actions",
  // PSC codes features
  pscActions: "psc-actions",
  pscTabs: "psc-tabs",
  pscRowActions: "psc-row-actions",
  // Ranks features
  ranksTemplate: "ranks-template",
  ranksImport: "ranks-import",
  ranksNew: "ranks-new",
} as const;

export interface TourStep {
  /** Unique id (stable across reorders — handy for analytics later). */
  id: string;
  /**
   * Anchor to highlight. Omit for a centered modal step (welcome / finish /
   * page steps whose anchor didn't render). An array tries each selector and
   * uses the first that's visible (e.g. desktop sidebar vs. mobile hamburger).
   */
  element?: string | string[];
  /**
   * Page to visit before showing the step, RELATIVE to the role base
   * (e.g. "/manage-inspections"). Omitted = run on the current page (shell).
   */
  route?: string;
  /**
   * Open the FIRST record of a kind and show the step on its detail page.
   * The provider reads the first row's link from the relevant list, navigates
   * there, and (for "inspection-questions") appends "/questions". Used to tour
   * detail-only areas (question workflow, rectification flow). Pair with
   * `requireElement` so the step skips when no records exist.
   */
  open?: "inspection" | "inspection-questions" | "observation" | "piq" | "piq-questions";
  title: string;
  description: string;
  /**
   * For a page sub-step whose target may not exist for this user (e.g. a
   * permission/rank-gated button): SKIP the step when the anchor isn't found,
   * instead of falling back to a centered popover. Use this for "feature"
   * highlights that would be misleading to show when the feature is absent.
   */
  requireElement?: boolean;
}

const sel = (anchor: string) => `[data-tour="${anchor}"]`;

/* ─────────────── Shell steps (not tied to a nav item) ─────────────── */

const WELCOME: TourStep = {
  id: "welcome",
  title: "Welcome to iCheck 👋",
  description:
    "Take a quick guided tour of the main areas. We'll hop between a few pages — press Next to continue, or close anytime. You can restart this later from your profile menu.",
};

const NAVIGATION: TourStep = {
  id: "navigation",
  element: [sel(TOUR_ANCHORS.nav), sel(TOUR_ANCHORS.mobileNav)],
  title: "Main navigation",
  description:
    "Move around iCheck from here — everything in your sidebar. On phones, tap the menu icon to open it.",
};

const SEARCH: TourStep = {
  id: "search",
  element: sel(TOUR_ANCHORS.search),
  title: "Quick search",
  description:
    "Jump straight to an inspection, vessel or page without hunting through menus.",
  // Hidden below md — the provider self-skips this step on mobile.
};

/* ─── Header controls (shell steps, present on every page) ─── */

const ASK_AI: TourStep = {
  id: "ask-ai",
  element: sel(TOUR_ANCHORS.askAi),
  requireElement: true, // admin-only + hidden on small screens — skip when absent
  title: "Ask AI",
  description:
    "Your AI assistant is one click away. Ask questions about your fleet's data and surface trends and risks across inspections, without leaving the page you're on.",
};

const NOTIFICATIONS: TourStep = {
  id: "notifications",
  element: sel(TOUR_ANCHORS.notifications),
  title: "Notifications",
  description:
    "Updates on inspections, reviews and rectifications land here. The badge shows how many are unread — open the bell for the latest, or view them all.",
};

const SYNC_STATUS: TourStep = {
  id: "sync-status",
  element: sel(TOUR_ANCHORS.syncStatus),
  title: "Online & sync status",
  description:
    "See whether you're online at a glance. Work offline and iCheck saves your changes locally, then syncs them automatically once you're back — click here to sync now.",
};

const THEME: TourStep = {
  id: "theme",
  element: sel(TOUR_ANCHORS.theme),
  title: "Light or dark",
  description:
    "Switch between light, dark and system-matched appearance to suit your surroundings — handy on the bridge at night.",
};

const PROFILE: TourStep = {
  id: "profile",
  element: sel(TOUR_ANCHORS.profile),
  title: "Your profile menu",
  description:
    "Open your profile and preferences, sign out, and relaunch this tour anytime — it all lives behind your avatar here.",
};

const FINISH: TourStep = {
  id: "finish",
  title: "You're all set",
  description:
    "That's the tour. Explore at your own pace — and remember you can restart it anytime from the profile menu.",
};

/* ─── Deep-dive sub-steps, spliced in right after their parent page step ─── */

const INSPECTION_SUBSTEPS: TourStep[] = [
  {
    id: "inspections-actions",
    route: "/manage-inspections",
    element: sel(TOUR_ANCHORS.inspectionsActions),
    requireElement: true, // gated by create/import permission — skip if absent
    title: "Start or import an inspection",
    description:
      "Begin a brand-new inspection here, or (for admins) import an existing report straight from a PDF to save manual entry.",
  },
  {
    id: "inspections-filters",
    route: "/manage-inspections",
    element: sel(TOUR_ANCHORS.inspectionsFilters),
    requireElement: true,
    title: "Find inspections fast",
    description:
      "Search by name and narrow the list by status, type, vessel or date — handy once your fleet builds up a history.",
  },
  {
    id: "inspection-detail",
    open: "inspection",
    element: sel(TOUR_ANCHORS.inspectionStatusActions),
    requireElement: true,
    title: "Inspection detail",
    description:
      "This is an inspection's home. The status and the key actions live here — submit it for review, and (for reviewers) approve, re-open or delete it.",
  },
  {
    id: "q-sidebar",
    open: "inspection-questions",
    element: sel(TOUR_ANCHORS.qSidebar),
    requireElement: true,
    title: "Question navigator",
    description:
      "Work through the inspection here. This sidebar lists every chapter and question with its answer status, so you can jump straight to what's left.",
  },
  {
    id: "q-filters",
    open: "inspection-questions",
    element: sel(TOUR_ANCHORS.qFilters),
    requireElement: true,
    title: "Focus the question set",
    description:
      "Filter questions by rank, ROVIQ element, answer status or risk to narrow down to just the ones you need to act on.",
  },
  {
    id: "q-tabs",
    open: "inspection-questions",
    element: sel(TOUR_ANCHORS.qTabs),
    requireElement: true,
    title: "Answer & supporting info",
    description:
      "Record your response here, add photos and attachments, and switch tabs for operator content and guidance to help you answer accurately.",
  },
  {
    id: "q-footer",
    open: "inspection-questions",
    element: sel(TOUR_ANCHORS.qFooter),
    requireElement: true,
    title: "Save & move on",
    description:
      "Move between questions and save your work here. Once every question is complete, submit the whole inspection for review.",
  },
];

const OBSERVATION_SUBSTEPS: TourStep[] = [
  {
    id: "observations-filters",
    route: "/observations",
    element: sel(TOUR_ANCHORS.observationsFilters),
    requireElement: true,
    title: "Filter your findings",
    description:
      "Zero in on what matters — filter by severity, status, aspect, inspection type or vessel, or group findings by vessel.",
  },
  {
    id: "observation-detail",
    open: "observation",
    element: sel(TOUR_ANCHORS.observationStatus),
    requireElement: true,
    title: "Observation detail",
    description:
      "Open a finding to see its full context. Its status and the review actions sit here — admins can send it back to the vessel, re-open or close it.",
  },
  {
    id: "observation-rectifications",
    open: "observation",
    element: sel(TOUR_ANCHORS.observationRectifications),
    requireElement: true,
    title: "Rectifications",
    description:
      "The vessel submits a corrective action here; the admin reviews it, returns it for changes if needed, and closes the observation once satisfied.",
  },
];

const PIQ_SUBSTEPS: TourStep[] = [
  {
    id: "piq-actions",
    route: "/pre-inspections",
    element: sel(TOUR_ANCHORS.inspectionsActions),
    requireElement: true, // "New PIQ" — gated by create permission, skips otherwise
    title: "Create a PIQ",
    description:
      "Each vessel has one Pre-Inspection Questionnaire — create or open it here to get it ready ahead of an inspection.",
  },
];

const RANK_SUBSTEPS: TourStep[] = [
  {
    id: "ranks-template",
    route: "/ranks",
    element: sel(TOUR_ANCHORS.ranksTemplate),
    requireElement: true,
    title: "Download the template",
    description:
      "Grab the Excel template, fill in your ranks, and it's ready to bulk-import.",
  },
  {
    id: "ranks-import",
    route: "/ranks",
    element: sel(TOUR_ANCHORS.ranksImport),
    requireElement: true,
    title: "Import from Excel",
    description:
      "Upload your filled-in template to add or update many ranks at once.",
  },
  {
    id: "ranks-new",
    route: "/ranks",
    element: sel(TOUR_ANCHORS.ranksNew),
    requireElement: true,
    title: "Add a rank",
    description:
      "Prefer to add just one? Create a single rank manually here.",
  },
];

const PSC_SUBSTEPS: TourStep[] = [
  {
    id: "psc-actions",
    route: "/psc-codes",
    element: sel(TOUR_ANCHORS.pscActions),
    requireElement: true,
    title: "Add a code",
    description:
      "Add a new deficiency or action code to the global catalogue every client shares.",
  },
  {
    id: "psc-tabs",
    route: "/psc-codes",
    element: sel(TOUR_ANCHORS.pscTabs),
    requireElement: true,
    title: "Browse the catalogue",
    description:
      "Switch between PSC regimes and between deficiency and action codes; the matching list shows below.",
  },
  {
    id: "psc-row-actions",
    route: "/psc-codes",
    element: sel(TOUR_ANCHORS.pscRowActions),
    requireElement: true,
    title: "Edit or remove a code",
    description:
      "Edit a code's details, or deactivate one you no longer want clients to use (reactivate it later if needed).",
  },
];

const COMPANY_SUBSTEPS: TourStep[] = [
  {
    id: "companies-actions",
    route: "/company-management",
    element: sel(TOUR_ANCHORS.companiesActions),
    requireElement: true,
    title: "Add a company",
    description:
      "Onboard a new operator company here — each gets its own vessels, users, branding and settings.",
  },
  {
    id: "companies-row-actions",
    route: "/company-management",
    element: sel(TOUR_ANCHORS.companiesRowActions),
    requireElement: true,
    title: "Manage a company",
    description:
      "Per company you can open its settings (branding, AI usage), edit its details, enable/disable it, or delete it.",
  },
];

const VESSEL_SUBSTEPS: TourStep[] = [
  {
    id: "vessels-actions",
    route: "/vessel-management",
    element: sel(TOUR_ANCHORS.vesselsActions),
    requireElement: true,
    title: "Add a vessel",
    description:
      "Add a new vessel, or import several at once. Vessels are what inspections, PIQs and observations attach to.",
  },
  {
    id: "vessels-search",
    route: "/vessel-management",
    element: sel(TOUR_ANCHORS.vesselsSearch),
    requireElement: true,
    title: "Find a vessel",
    description:
      "Search your fleet by name, IMO or code to jump straight to a vessel.",
  },
];

/* ─────────────── Per-page copy, keyed by RELATIVE route ─────────────── */

interface PageCopy {
  /** Defaults to the sidebar label when omitted. */
  title?: string;
  description: string;
  /** Extra steps shown right after this page (filters, deep-dives). */
  sub?: TourStep[];
}

/**
 * Prose for each sidebar page, keyed by its route relative to the role base
 * (e.g. "/manage-inspections"). A nav item with no entry here is simply not
 * toured. Order / role / visibility all come from nav-config — not from here.
 */
const PAGE_COPY: Record<string, PageCopy> = {
  "/dashboard": {
    title: "Your dashboard",
    description:
      "Your starting point: at-a-glance KPIs, fleet status and recent activity so you can spot what needs attention first.",
  },
  "/ai": {
    description:
      "Ask questions about your fleet's data and surface trends and risks across inspections — your AI assistant for analysis.",
  },
  "/manage-inspections": {
    title: "Inspections",
    description:
      "All your inspections live here — each row shows its status and completion progress. Search and filter to find one, then open it to work through its questions and record findings.",
    sub: INSPECTION_SUBSTEPS,
  },
  "/pre-inspections": {
    description:
      "Each vessel's Pre-Inspection Questionnaire lives here — complete it ahead of an inspection to capture vessel particulars and readiness.",
    sub: PIQ_SUBSTEPS,
  },
  "/observations": {
    title: "Observations & rectifications",
    description:
      "Findings raised during inspections are collected here. Open one to follow its rectification through to close-out.",
    sub: OBSERVATION_SUBSTEPS,
  },
  "/vessel-management": {
    description:
      "Maintain your fleet here — add vessels and keep their particulars up to date. Vessels are what inspections, PIQs and observations are tied to.",
    sub: VESSEL_SUBSTEPS,
  },    
  "/user-management": {
    description:
      "Invite shore staff and crew, and control what each person can see and do with role-based permissions — all managed from here.",
  },
  "/checklist-management": {
    description:
      "The question templates that power inspections live here. Build and version checklists so every inspection uses a consistent set of questions.",
  },
  "/root-cause-categories": {
    description:
      "Curate the root-cause categories inspectors pick from when logging observations — keeping your analytics consistent and comparable.",
  },
  "/company-management": {
    description:
      "Onboard and manage the operator companies on iCheck — each with its own vessels, users, branding and settings.",
    sub: COMPANY_SUBSTEPS,
  },
  "/psc-codes": {
    description:
      "Manage the PSC deficiency codes used across iCheck so inspections and observations all reference a consistent code set.",
    sub: PSC_SUBSTEPS,
  },
  "/ranks": {
    description:
      "Maintain the crew ranks used throughout iCheck — these drive rank-based access and the rank filter on inspection questions.",
    sub: RANK_SUBSTEPS,
  },
  "/risk-heat-map": {
    description:
      "See where risk concentrates across your fleet at a glance — vessels and categories shaded by severity so the hotspots stand out.",
  },
  "/cycle-comparison": {
    description:
      "Compare performance across inspection cycles to see whether findings are trending up or down over time.",
  },
  "/reports": {
    description:
      "Generate periodic reports that summarise inspections, observations and rectifications for sharing and record-keeping.",
  },
  "/import-data": {
    description:
      "Bring historical inspection data into iCheck so your analytics reflect your full history, not just what's logged here.",
  },
  "/notifications": {
    description:
      "Updates on inspections, reviews and rectifications appear here. The bell in the header shows unread counts so you never miss an action.",
  },
  "/settings": {
    title: "Profile, settings & help",
    description:
      "Manage your profile and preferences here (admins also get company settings). The Help Center is in the sidebar — and you can relaunch this tour anytime from the profile menu.",
  },
};

/** Strip the role base off an absolute nav `to` to get its PAGE_COPY key. */
function relKey(to: string, base: string): string {
  return base && to.startsWith(base) ? to.slice(base.length) : to;
}

/**
 * Build the ordered tour for a role, DERIVED from its sidebar:
 *   welcome → navigation → search → [each visible nav page (+ its sub-steps)] → finish
 *
 * `canViewFeature` mirrors the sidebar's RBAC gating so the tour never visits a
 * page the user can't see. Defaults to "show everything" for safety.
 */
export function buildTourSteps(
  role: Role | null | undefined,
  canViewFeature: (feature: string) => boolean = () => true,
): TourStep[] {
  if (!role) return [WELCOME, FINISH];

  const base = ROLE_BASE[role] ?? "";
  const navItems = [
    ...getNavForRole(role).flatMap((g) => g.items),
    ...SECONDARY_ITEMS(role),
  ]
    // Respect RBAC feature visibility, exactly like the sidebar does.
    .filter((it) => !it.feature || canViewFeature(it.feature))
    // Help is a public route OUTSIDE the authenticated shell; visiting it would
    // unmount the tour, so it's never a tour step.
    .filter((it) => it.to !== "/help");

  const pageSteps = navItems.flatMap((it) => {
    const key = relKey(it.to, base);
    const copy = PAGE_COPY[key];
    if (!copy) return []; // nav item we don't tour
    const page: TourStep = {
      id: key,
      route: key,
      element: sel(TOUR_ANCHORS.content),
      title: copy.title ?? it.label,
      description: copy.description,
    };
    return [page, ...(copy.sub ?? [])];
  });

  return [
    WELCOME,
    NAVIGATION,
    SEARCH,
    ASK_AI,
    NOTIFICATIONS,
    SYNC_STATUS,
    THEME,
    PROFILE,
    ...pageSteps,
    FINISH,
  ];
}

/** Absolute path for a step's route, given the user's role base. */
export function stepPath(route: string, role: Role | null | undefined): string {
  const base = ROLE_BASE[(role ?? "vessel") as Role] ?? "";
  return `${base}${route}`;
}
